import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';

import { authenticatedAPICall } from '../../client/api-client';
import { Product } from '../../types/types';

@customElement('search-dropdown')
export class SearchDropdown extends LitElement {
  static styles = css`
    :host {
      width: 100%;
    }
    .wrapper {
      position: relative;
    }

    .inner-display {
      width: calc(100% - 10px);
    }

    .row {
      display: flex;
      flex-direction: row;
      width: 100%;
    }

    .column {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .selectables {
      position: absolute;
      z-index: 99;
      color: var(--dark-background-color);
      background-color: white;
      display: none;
      width: 100%;
      border: 1px solid var(--dark-background-color);
      border-radius: 4px;
      max-height: 300px;
      overflow-y: auto;
    }

    :host([selectablesVisible]) .selectables {
      display: block;
    }

    input {
      width: calc(100% - 4px);
      height: 22px;
      padding: 5px;
      border-radius: 5px;
      font-size: 16px;
    }

    .search-option {
      border-bottom: 1px solid var(--dark-background-color);
    }

    .search-option:hover {
      color: white;
      background-color: var(--text-color-main);
    }
  `;

  @property({ type: Array, attribute: true })
  public selectable: Product[] = [];

  @property({ type: Object, attribute: true, reflect: true })
  public selected?: Product;

  @property({ type: String, attribute: true, reflect: true })
  public productCodeFilter = '';

  @property({ type: Boolean, reflect: true })
  public selectablesVisible = false;

  @query('#searchInputProduct')
  public searchInput?: HTMLInputElement;

  public isRetour = false;

  constructor() {
    super();
    document.addEventListener('click', this._handleClick);
  }

  protected firstUpdated(): void {
    if (this.productCodeFilter == '') {
      this.listProducts();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this._handleClick);
  }

  // nolint
  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ) {
    super.attributeChangedCallback(name, _old, value);
    if (name === 'productcodefilter' && value != '') {
      this.listProducts();
    }
  }

  _handleClick = (e: Event) => {
    // If not cur element is clicked
    if (!e.composedPath().includes(this)) {
      this.selectablesVisible = false;
      if (this.searchInput) {
        this.searchInput.value = this.getInputValue();
      }
    }
  };

  render() {
    return html`
      <div class="wrapper">
        <div class="row selected-display">
          <div class="inner-display">
            <input
              type="text"
              id="searchInputProduct"
              name="searchInputProduct"
              value="${this.getInputValue()}"
              @click="${() => {
                this.selectablesVisible = !this.selectablesVisible;
                if (this.searchInput && this.selectablesVisible) {
                  this.searchInput.value = '';
                }
              }}"
              @keydown=${() => {
                this.selectablesVisible = true;
                this.listWithBackoff();
              }}
            />
          </div>
        </div>
        <div class="selectables">
          <div class="column">${this.renderSelectables()}</div>
        </div>
      </div>
    `;
  }

  renderSelectables = () => {
    if (this.selectable.length === 0) {
      return html`
        <div class="search-option">No products found for current search.</div>
      `;
    }
    return html`
      ${this.selectable.map(item => {
        return html`
          <div
            class="search-option"
            @click=${() => {
              this.selectablesVisible = false;
              this.selected = item;
              if (this.searchInput) {
                this.searchInput.value = item.description;
              }
              let selectedProduct = {};
              if (this.selected) {
                selectedProduct = this.selected;
              }
              const event = new CustomEvent('change', {
                detail: {
                  value: JSON.stringify(selectedProduct),
                },
              });
              this.dispatchEvent(event);
            }}
          >
            ${item.description}
          </div>
        `;
      })}
    `;
  };

  getInputValue = () => {
    if (this.selected) {
      return this.selected.description;
    }
    return 'no product selected';
  };

  listWithBackoff = () => {
    setTimeout(() => {
      this.listProducts();
    }, 10);
  };

  listProducts = async () => {
    const filterParts = [];
    if (
      this.searchInput &&
      this.searchInput.value &&
      this.searchInput.value !== 'no product selected'
    ) {
      if (this.selected?.description !== this.searchInput.value) {
        filterParts.push(`filter=${this.searchInput.value}`);
      }
    }
    if (this.productCodeFilter !== '') {
      filterParts.push(`code_filter=${this.productCodeFilter}`);
    }
    if (this.isRetour) {
      filterParts.push(`retour_order=true`);
    }

    const result = await authenticatedAPICall(
      `/products?${filterParts.join('&')}`,
      'GET',
      new Headers(),
    );
    if (result.statusCode !== undefined && result.statusCode >= 200 && result.statusCode < 300 && Array.isArray(result.body)) {
      this.selectable = result.body as Product[];
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'search-dropdown': SearchDropdown;
  }
}
